import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useToast } from '@/shared/toast';
import type { Compartment } from './types';

/**
 * Modal "Controllo Caricamento": l'utente fleagga tutti gli oggetti presenti
 * a bordo del mezzo. Submit POST /vehicles/loading-check. Lista vani
 * collassabili (default chiusi) per non avere liste lunghissime aperte.
 */
export function VehicleLoadingCheckModal({ vehicleId, compartments, onClose }: {
  vehicleId: string; compartments: Compartment[]; onClose: () => void;
}): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const allItems = compartments.flatMap(c => (c.items ?? []).map(it => ({ ...it, compartmentName: c.name, compartmentId: c.id })));
  const [checked, setChecked] = useState<Record<string, boolean>>(
    Object.fromEntries(allItems.map(it => [it.id, true]))
  );
  // Bryan: vani e sottovani COLLASSABILI nel modal, default chiusi per non
  // mostrare liste lunghissime tutte aperte.
  const [expandedComp, setExpandedComp] = useState<Record<string, boolean>>({});
  // Raggruppo gli items per compartment per renderli collapsabili
  const grouped = compartments
    .filter(c => (c.items?.length ?? 0) > 0)
    .map(c => ({
      comp: c,
      items: (c.items ?? []).map(it => ({ ...it, compartmentName: c.name, compartmentId: c.id })),
    }));

  function toggleAll(value: boolean): void {
    setChecked(Object.fromEntries(allItems.map(it => [it.id, value])));
  }

  async function save(): Promise<void> {
    try {
      const checklist = allItems.map(it => ({
        itemId: it.id, name: it.name, present: checked[it.id] ?? false,
      }));
      await api.post('/vehicles/loading-check', {
        vehicleId, checklistJson: JSON.stringify(checklist),
      });

      // Bryan: ogni oggetto non spuntato genera una segnalazione "mancante"
      // automatica. Le faccio in parallelo per velocita'; gli eventuali errori
      // non bloccano il flusso (il check principale e' gia' salvato).
      const missing = allItems.filter(it => !checked[it.id]);
      const reportResults = await Promise.allSettled(
        missing.map(it => api.post('/reports', {
          itemId: it.id, note: 'mancante',
        }))
      );
      const failed = reportResults.filter(r => r.status === 'rejected').length;

      qc.invalidateQueries({ queryKey: ['vehicle.checks', vehicleId] });
      qc.invalidateQueries({ queryKey: ['reports.counts'] });

      if (missing.length > 0) {
        toast.show(
          `Controllo salvato · ${missing.length - failed} segnalazion${missing.length - failed === 1 ? 'e' : 'i'} "mancante" creat${missing.length - failed === 1 ? 'a' : 'e'}` +
          (failed > 0 ? ` (${failed} errore/i)` : ''),
          failed > 0 ? 'info' : 'success'
        );
      } else {
        toast.show('Controllo caricamento salvato', 'success');
      }
      onClose();
    } catch {
      toast.show('Errore salvataggio', 'error');
    }
  }

  const missingCount = allItems.filter(it => !checked[it.id]).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <h3>📋 Controllo Caricamento</h3>
        <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
          Spunta gli oggetti presenti sul mezzo. Quelli non spuntati verranno marcati come mancanti.
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button onClick={() => toggleAll(true)}
            style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6, cursor: 'pointer',
                     background: '#28a745', color: 'white' }}>
            ✓ Tutti presenti
          </button>
          <button onClick={() => toggleAll(false)}
            style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6, cursor: 'pointer',
                     background: '#dc3545', color: 'white' }}>
            ✗ Tutti mancanti
          </button>
        </div>

        <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: 8, padding: 8, marginBottom: 16 }}>
          {grouped.length === 0 && <div style={{ textAlign: 'center', color: '#888', padding: 20 }}>Nessun oggetto registrato</div>}
          {grouped.map(g => {
            const isOpen = !!expandedComp[g.comp.id];
            const compMissing = g.items.filter(it => !checked[it.id]).length;
            const compTotal = g.items.length;
            return (
              <div key={g.comp.id} className="vano-card">
                <button type="button" className="vano-toggle"
                  onClick={() => setExpandedComp({ ...expandedComp, [g.comp.id]: !isOpen })}>
                  <span>{isOpen ? '▼' : '▶'} 📁 {g.comp.name}</span>
                  <span style={{ fontSize: 12, color: compMissing > 0 ? '#dc3545' : '#28a745' }}>
                    {compTotal - compMissing}/{compTotal} presenti
                  </span>
                </button>
                {isOpen && g.items.map(it => (
                  <label key={it.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                    cursor: 'pointer',
                    background: checked[it.id] ? '#d4edda' : '#f8d7da',
                    borderTop: '1px solid #e9ecef',
                  }}>
                    <input type="checkbox" checked={checked[it.id] ?? false}
                      onChange={e => setChecked({ ...checked, [it.id]: e.target.checked })}
                      style={{ width: 18, height: 18 }} />
                    <span style={{ flex: 1, fontSize: 14 }}>
                      <strong>{it.name}</strong> ({it.quantity} {it.unit || 'pz'})
                    </span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>

        {missingCount > 0 && (
          <div style={{ background: '#fff3cd', border: '1px solid #ffeaa7', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            ⚠️ <strong>{missingCount}</strong> oggetto/i risulterà/anno mancante/i sul mezzo.
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn-secondary" style={{ padding: '10px 20px', fontWeight: 600 }}>Annulla</button>
          <button onClick={save} disabled={allItems.length === 0}
            style={{ padding: '10px 24px', fontWeight: 700, border: 'none', borderRadius: 8, cursor: allItems.length === 0 ? 'not-allowed' : 'pointer',
                     background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', color: 'white',
                     boxShadow: '0 2px 6px rgba(40, 167, 69, 0.3)' }}>
            💾 Salva Controllo
          </button>
        </div>
      </div>
    </div>
  );
}

